<template>
  <InfuseModal
    :title="role ? t('iam.roles.header.update_role') : t('iam.roles.header.create_role')"
    @close="emit('close')"
    :disable-close="loading"
  >
    <form id="update-role" @submit.prevent="onSubmit" class="flex flex-col gap-2">
      <InfuseField :label="t('iam.roles.name')">
        <InfuseInput
          :required="true"
          v-model="nameMutation"
          ref="nameInputRef"
          :placeholder="t('iam.roles.name_placeholder')"
        />
      </InfuseField>

      <InfuseField :label="t('iam.roles.description')">
        <InfuseInput
          :required="true"
          v-model="descriptionMutation"
          ref="nameInputRef"
          :placeholder="t('iam.roles.description_placeholder')"
        />
      </InfuseField>

      <InfuseField :label="t('iam.roles.scope')">
        <div class="flex flex-col gap-2">
          <InfuseSegmentedControl
            v-if="!organisationId"
            :model-value="sharedMutation"
            :options="[
              { value: false, label: () => unsharedText },
              { value: true, label: () => sharedText }
            ]"
            @update:modelValue="(value: string | boolean) => (sharedMutation = value as boolean)"
          />

          <InfuseNote v-if="showSharedWarning" type="warn">
            {{ t('iam.roles.warning.unshare_role') }}
          </InfuseNote>

          <div class="text-sm text-neutral-500 overflow-wrap w-5/6">
            {{ scopeDescription }}
          </div>
        </div>
      </InfuseField>
    </form>
    <template #actions>
      <InfuseButton color="secondary" type="button" @click="emit('close')" :disabled="loading">
        {{ t('action.cancel') }}
      </InfuseButton>

      <InfuseButton type="submit" form="update-role" :disabled="loading" :loading="loading">
        {{ t('action.submit') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { CreateRole, Role, UpdateRole } from '@infuse/iam/role/type';
import {
  InfuseButton,
  InfuseField,
  InfuseInput,
  InfuseModal,
  InfuseNote,
  InfuseSegmentedControl
} from '@infuse/ui/components';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getScopeText, getScopeTooltip } from './roleScope';

const { t } = useI18n();

const nameMutation = ref('');
const descriptionMutation = ref('');
const sharedMutation = ref(false);

const props = defineProps<{
  role?: Role;
  loading?: boolean;
  organisationId?: string;
  tenantId?: string;
}>();

// Create a computed role object for scope calculation
const computedRole = computed<Role>(() => {
  if (props.role) {
    return {
      ...props.role,
      shared: sharedMutation.value
    };
  }
  // For new roles, use the provided organisationId/tenantId props
  const baseRole: Partial<Role> = {
    shared: sharedMutation.value,
    organisationId: props.organisationId,
    tenantId: props.tenantId
  };
  return baseRole as Role;
});

const sharedText = computed(() => getScopeText({ ...computedRole.value, shared: true }, t));
const unsharedText = computed(() => getScopeText({ ...computedRole.value, shared: false }, t));
const scopeDescription = computed(() => getScopeTooltip(computedRole.value, t));

const showSharedWarning = computed(() => {
  return props.role?.shared === true && sharedMutation.value === false;
});

interface Emits {
  (event: 'close'): void;

  (event: 'mutate', vars: { id?: string; role: UpdateRole | CreateRole }): void;
}

const emit = defineEmits<Emits>();

watch(
  () => props.role,
  (role) => {
    if (!role) {
      return;
    }

    nameMutation.value = role.name;
    descriptionMutation.value = role.description;
    sharedMutation.value = role.shared ?? false;
  },
  { immediate: true }
);

const onSubmit = () => {
  emit('mutate', {
    id: props.role?.id,
    role: {
      name: nameMutation.value,
      description: descriptionMutation.value,
      shared: sharedMutation.value,
      version: props.role?.version
    }
  });
};
</script>
